import { Box, Checkbox, FormControlLabel, FormGroup, Typography } from "@mui/material";
import { debounce } from "lodash";
import React, { ReactElement, useEffect, useState } from "react";

import { CapitalAccountsFilter } from "../../../utils/types/capitalAccounts.type";
import { Option } from "../../../utils/types/filter.type";
import { inlineFilterTypes } from "../../../utils/types/listItems";
import SearchBar from "../../SearchBarWithDebounce/SearchBar";
import { ButtonText, FormControlCheckAll } from "./Filter.style";
import {
  FilterAll,
  FilterCheckBox,
  FilterContent,
  FilterItem,
  FundsContent,
  SplitFilterContent,
} from "./PopoverFilter.style";

type Props = {
  handleFilter?: (filter: CapitalAccountsFilter[], selected: string[][]) => void;
  splitFilters: inlineFilterTypes[];
  clearable?: boolean;
};

type OptionItemProps = {
  filterName: string;
  id: string;
  label: string;
  onSelect: (item: Option, checked: boolean, name: string) => void;
  selected: boolean;
}

const OptionItem: React.FC<OptionItemProps> = ({
  filterName,
  id,
  label,
  onSelect,
  selected = false,
}: OptionItemProps): ReactElement => {

  const onCheck = (e: any): void => {
    const isChecked = e.target.checked;

    onSelect({
      id,
      name: label
    }, isChecked, filterName);
  };

  return (
    <FilterItem>
      <FormControlLabel
        control={
          <FilterCheckBox
            id={`${id}_check`}
            name={label}
            checked={selected}
            onChange={onCheck}
          />
        }
        label={label}
      />
    </FilterItem>
  );
};

const SplitFilter: React.FC<Props> = ({
  clearable = true,
  splitFilters,
  handleFilter
}: Props): ReactElement => {
  const [search, setSearch] = useState<any>();
  const [selectedOptionList, setSelectedOptionList] = useState<any>();
  const [filteredOptionList, setFilteredOptionList] = useState<any>();

  const filterSelectedOptions: any = {};
  const filterSearchKeys: any = {};
  const filteredOptions: any = {};

  splitFilters.forEach(item => {
    filterSearchKeys[item.inlineFilterName] = "";
    filterSelectedOptions[item.inlineFilterName]= item.inlineFilterSelected;
    filteredOptions[item.inlineFilterName] = item.inlineFilterOptions;
  });

  useEffect(() => {
    setSelectedOptionList(filterSelectedOptions);
    setSearch(filterSearchKeys);
  }, []);

  useEffect(() => {
    setFilteredOptionList(filteredOptions);
  }, []);

  const onSelect = (item: Option, checked: boolean, name: string): void => {
    const new_option_id = item.id;
    let selected_options = [...(selectedOptionList?.[name] || [])];

    if (checked) {
      selected_options.push(new_option_id);
    } else {
      selected_options = selected_options.filter(option => option !== new_option_id);
    }
    setSelectedOptionList((prevSelectedOptionsList: any) => ({ ...prevSelectedOptionsList, [name]: selected_options }));
  };

  const onCheckAll = (checked: boolean, name: string) => {
    const curSplitFilter = splitFilters.filter(
      filter => filter.inlineFilterName === name
    )[0];

    if (checked) {
      const allChecked = curSplitFilter.inlineFilterOptions?.map(
        (item) => item[curSplitFilter.inlineFilterIDField]) ?? [];

      setSelectedOptionList((prevSelectedOptions: any) => ({ ...prevSelectedOptions, [name]: allChecked }));
    } else {
      setSelectedOptionList((prevSelectedOptions: any) => ({ ...prevSelectedOptions, [name]: [] }));
    }
  };

  const onSearch = (value: string, name: string) => {
    const currentFilter = splitFilters.find(
      filter => filter.inlineFilterName === name
    );

    if(currentFilter) {
      const {
        inlineFilterLabelField: label,
        inlineFilterOptions: options,
      } = currentFilter;
  
      const regex = new RegExp(value, "igm");
      const listFilter = options.filter(item => item[label].search(regex) !== -1);

      setFilteredOptionList((prevSelectedOptions: any) => ({ ...prevSelectedOptions, [name]: listFilter }));
      setSearch((prevSearchKeys: any) => ({ ...prevSearchKeys, [name]: value }));
    }
  };

  const onClear = () => {
    const selectedOptions: any = {};
    const searchKeys: any = {};

    splitFilters.forEach(filter => {
      selectedOptions[filter.inlineFilterName] = filter.inlineFilterOptions.map(f => f[filter.inlineFilterIDField]) || [];
      searchKeys[filter.inlineFilterName] = "";
    });
    setSearch(searchKeys);
    setSelectedOptionList(selectedOptions);
  };

  const handleApply = () => {
    const { filter, selected } = splitFilters.reduce((accumulator: any, f) => {
      return {
        filter: [...accumulator.filter, f.inlineFilterName],
        selected: [...accumulator.selected, selectedOptionList[f.inlineFilterName]]
      };
    }, { filter: [], selected: [] });

    handleFilter?.(filter, selected);
  };

  return search ? (
    <div id="popover_filter_text">
      <SplitFilterContent>
        {
          splitFilters.map((filter, index) => {
            const {
              inlineFilterName: name,
              inlineFilterOptions: options,
              inlineFilterLabelField: labelField,
              inlineFilterIDField: idField,
              inlineFilterTitle: title,
            } = filter;

            const isAllSelected: boolean = selectedOptionList[name].length === options?.length;

            return (
              <FilterContent key={name}>
                {title && (<Typography variant="formTitle2">
                  {title}
                </Typography>)}
                <SearchBar
                  id={`filter_search_${name}`}
                  onChange={(value: any) => 
                    debounce((value) => onSearch(value || "", name), 700)(value)
                  }
                  value={search[name] || ""}
                />
                <FormGroup>
                  <FilterAll>
                    <b>
                      <FormControlCheckAll
                        id={"check_all_" + name}
                        key="All"
                        control={
                          <Checkbox
                            checked={isAllSelected}
                            indeterminate={
                              options &&
                              selectedOptionList[name].length > 0 &&
                              selectedOptionList[name].length < options?.length
                            }
                            onChange={(_, checked) => onCheckAll(checked, name)}
                          />
                        }
                        label="All"
                      />
                    </b>
                  </FilterAll>
                  <FundsContent>
                    {
                      options && options.length > 0
                        ? (
                          filteredOptionList[name].reduce((accumulator: Record<string, any>[], current: any) => {
                            if (
                              (search &&
                                current[labelField]
                                  .toLowerCase()
                                  .includes(search[name].toLowerCase())) ||
                              !search
                            ) {
                              return [
                                ...accumulator,
                                <OptionItem
                                  filterName={name}
                                  key={current[idField]}
                                  label={current[labelField]}
                                  id={current[idField]}
                                  onSelect={onSelect}
                                  selected={selectedOptionList[name]?.includes(current[idField])}
                                />
                              ];
                            }
                            return accumulator;
                          }, [])
                        ): (
                          <div>No records</div>
                        )
                    }
                  </FundsContent>
                </FormGroup>
              </FilterContent>
            );
          })
        }
      </SplitFilterContent>
      <Box display="flex" justifyContent="flex-end">
        <ButtonText id="btn_apply" variant="text" onClick={handleApply}>
          Apply Filter
        </ButtonText>
        {clearable && (
          <ButtonText id="btn_clear" variant="text" onClick={onClear}>
            Clear Filter
          </ButtonText>
        )}
      </Box>
    </div>
  ) : (
    <div>No records</div>
  );
};

export default SplitFilter;
